import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { Loader } from "@themes/pollinator/ui/Loader";
import {
  useFetchActions,
  getAction,
  isActionAvailable,
  isActionCompleted,
  useUpdateActionStatus,
  ACTION_STATUS,
  ACTION_TYPE_SLUG,
  type Action,
  type ScheduleActionDisplayOptions,
} from "@core/features/actions";
import { isCircleVisible, useFetchUserCircles } from "@core/features/circle";
import { usePlatformStore } from "@platform/store";
import {
  SchedulingContainer,
  ProgramCalendarEventListContainer,
  SchedulingCohortMessageContainer,
} from "@platform/features/programScheduling";
import { SchedulingTimezoneMessageContainer } from "@platform/features/programScheduling/components/SchedulingTimezoneMessage";

export type Props = {
  /** Program ID to load schedule data for */
  programId: number;
};

/**
 * Schedule tab content for the Program page.
 * Contains weekly availability scheduling and calendar events.
 */
export function ScheduleTabContent({ programId }: Props) {
  const navigate = useNavigate();
  const user = usePlatformStore((s) => s.user);

  // Fetch user actions to get the schedule action and its userPhaseActionId
  const { data: actionsData, isLoading: isLoadingActions } = useFetchActions({
    programId,
    user,
    isEnabled: !!user && !!programId,
  });

  // Fetch user circles to determine whether a visible cohort exists for this user
  const { data: circlesData } = useFetchUserCircles({ user, programId });
  const hasVisibleCircle = useMemo(
    () => (circlesData?.circles ?? []).some(isCircleVisible),
    [circlesData?.circles]
  );

  // Find the schedule action for this program
  const scheduleAction = useMemo(() => {
    if (!actionsData?.actions) return null;
    return actionsData.actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.SCHEDULE
    );
  }, [actionsData?.actions]);

  // Find the calendar_add action for this program
  const calendarAddAction = useMemo(() => {
    if (!actionsData?.actions) return null;
    return getAction(actionsData.actions, ACTION_TYPE_SLUG.CALENDAR_ADD);
  }, [actionsData?.actions]);

  // Check if calendar_add action is available
  const isCalendarAddAvailable = useMemo(() => {
    return calendarAddAction ? isActionAvailable(calendarAddAction) : false;
  }, [calendarAddAction]);

  // Extract scheduling config from action's display_options
  const schedulingConfig = useMemo((): ScheduleActionDisplayOptions | null => {
    if (!scheduleAction) return null;
    const displayOptions =
      scheduleAction.display_options as ScheduleActionDisplayOptions | null;
    return {
      scheduled_engagement_start_date:
        displayOptions?.scheduled_engagement_start_date,
      mode: displayOptions?.mode || "freeform",
      selectableOptions: displayOptions?.selectableOptions || [],
      requiredSelections: displayOptions?.requiredSelections || 0,
      maxSelections: displayOptions?.maxSelections,
      headerFormat: displayOptions?.headerFormat || "weekday",
    };
  }, [scheduleAction]);

  // Mutation to mark schedule action as completed on form submit
  const updateActionStatus = useUpdateActionStatus({
    actionId: scheduleAction?.id ?? 0,
    status: ACTION_STATUS.COMPLETED,
  });

  // Determine if the schedule action is closed (past its closed_date)
  const isReadOnly = scheduleAction ? !isActionAvailable(scheduleAction) : false;

  // Show loader while fetching action data
  if (isLoadingActions || !schedulingConfig) {
    return <Loader isFullPage />;
  }

  const scheduleTitle = isReadOnly ? "Your Schedule" : "Friday Availability";
  const scheduleDescription = isReadOnly
    ? "Your availability has been submitted."
    : "Select the time when you are available to meet with your cohort.";

  const scheduleSection = (
    <SectionHeading
      title={scheduleTitle}
      description={
        hasVisibleCircle ? (
          <SchedulingCohortMessageContainer />
        ) : (
          scheduleDescription
        )
      }
    >
      {!hasVisibleCircle && (
        <SchedulingContainer
          programId={programId}
          scheduledEngagementStartDate={
            schedulingConfig.scheduled_engagement_start_date
          }
          mode={schedulingConfig.mode}
          selectableOptions={schedulingConfig.selectableOptions}
          requiredSelections={schedulingConfig.requiredSelections}
          maxSelections={schedulingConfig.maxSelections}
          headerFormat={schedulingConfig.headerFormat}
          onSuccess={() => {
            if (scheduleAction && !isActionCompleted(scheduleAction)) {
              updateActionStatus.mutate();
            }
          }}
          onError={() => navigate("/error")}
          variant="inline"
          useDropdownActions
          primaryButtonText="Save"
          className="w-full flex flex-col gap-6"
          isReadOnly={isReadOnly}
        />
      )}
    </SectionHeading>
  );

  return (
    <OuterContentContainer className="flex flex-1" innerClassName="gap-12">
      <div className="flex flex-col gap-12 max-w-3xl">
        {hasVisibleCircle && scheduleSection}

        {isReadOnly && isCalendarAddAvailable && calendarAddAction && (
          <SectionHeading
            title="Meeting Schedule"
            description="View your upcoming meetings."
          >
            {scheduleAction && isActionCompleted(scheduleAction) && (
              <ProgramCalendarEventListContainer
                calendarAddAction={calendarAddAction}
              />
            )}
          </SectionHeading>
        )}

        {!hasVisibleCircle && scheduleSection}

        <SectionHeading
          title="Timezone Display"
          description={<SchedulingTimezoneMessageContainer />}
        />

        {!isReadOnly && isCalendarAddAvailable && calendarAddAction && (
          <SectionHeading
            title="Calendar Events"
            description={
              scheduleAction && isActionCompleted(scheduleAction)
                ? "Add your Pollinator meetings to your calendar."
                : "Select your Friday availability above to see your individual meeting times."
            }
          >
            {scheduleAction && isActionCompleted(scheduleAction) && (
              <ProgramCalendarEventListContainer
                calendarAddAction={calendarAddAction}
              />
            )}
          </SectionHeading>
        )}
      </div>
    </OuterContentContainer>
  );
}

export default ScheduleTabContent;
